<template>
  <div class="page-container">

    <!-- Header -->
    <div class="page-title-container d-flex align-items-center justify-content-between flex-wrap">
      <h1 class="page-title mb-0">Resumen de asistencia</h1>
      <div class="d-flex align-items-center mt-2 mt-md-0">
        <button type="button" class="button button-light button-sm mr-2" @click="loadData">
          <i class="ri-refresh-line mr-1"></i>Recargar
        </button>
        <button type="button" class="button button-primary button-sm" @click="downloadXlsx" :disabled="downloading">
          <i class="ri-file-excel-line mr-1"></i>
          <span v-if="downloading">Descargando...</span>
          <span v-else>Descargar Excel</span>
        </button>
      </div>
    </div>

    <!-- Filtros -->
    <div class="d-flex flex-wrap align-items-center mb-3 summary-filters">
      <div class="filter-field mr-3 mb-2">
        <label class="label-filter mb-1">Mes</label>
        <select class="form-control form-control-sm" v-model="filters.month" @change="loadData">
          <option v-for="(m, idx) in monthNames" :key="idx" :value="idx + 1">{{ m }}</option>
        </select>
      </div>
      <div class="filter-field mr-3 mb-2">
        <label class="label-filter mb-1">Año</label>
        <select class="form-control form-control-sm" v-model="filters.year" @change="loadData">
          <option v-for="y in years" :key="y" :value="y">{{ y }}</option>
        </select>
      </div>
      <div class="filter-field mr-3 mb-2">
        <label class="label-filter mb-1">Empresa</label>
        <SearchableDropdown
          :options="companies"
          :oldValue="filters.companyId"
          placeholder="Todas"
          @selected="onCompanySelected"
        />
      </div>
      <div class="filter-field mr-3 mb-2">
        <label class="label-filter mb-1">Área</label>
        <SearchableDropdown
          :options="areas"
          :oldValue="filters.areaId"
          placeholder="Todas"
          @selected="onAreaSelected"
        />
      </div>
    </div>

    <!-- Tabla -->
    <div class="table-responsive">
      <table class="custom-table">
        <thead>
          <tr>
            <th>EMPLEADO</th>
            <th>DNI</th>
            <th>EMPRESA</th>
            <th>ÁREA</th>
            <th class="text-center">DÍAS LAB.</th>
            <th class="text-center">DÍAS TRAB.</th>
            <th class="text-center">VACAC.</th>
            <th class="text-center">DM</th>
            <th class="text-center">FALTAS JUST.</th>
            <th class="text-center">FALTAS INJUST.</th>
            <th class="text-center">FERIADOS</th>
            <th class="text-center">DESCANSOS</th>
            <th class="text-center">PERMISOS</th>
            <th class="text-center">MIN. TARDANZA</th>
            <th class="text-center">HRS EXTRA</th>
            <th class="text-center">HRS TRABAJADAS</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="rows.length === 0">
            <td colspan="16" class="text-center text-muted py-4">
              No hay datos para el período seleccionado
            </td>
          </tr>
          <tr v-for="(row, idx) in rows" :key="idx">
            <td>{{ row.employeeName }}</td>
            <td>{{ row.employeeIdentifier }}</td>
            <td>{{ row.companyName }}</td>
            <td>{{ row.areaName }}</td>
            <td class="text-center">{{ row.workableDays }}</td>
            <td class="text-center">{{ row.daysWorked }}</td>
            <td class="text-center">{{ row.vacationDays }}</td>
            <td class="text-center">{{ row.medicalLeaveDays }}</td>
            <td class="text-center">{{ row.justifiedAbsences }}</td>
            <td class="text-center">{{ row.unjustifiedAbsences }}</td>
            <td class="text-center">{{ row.holidays }}</td>
            <td class="text-center">{{ row.restDays }}</td>
            <td class="text-center">{{ row.permissionDays }}</td>
            <td class="text-center">{{ row.totalTardinessMinutes }}</td>
            <td class="text-center">{{ row.totalOvertimeHours }}</td>
            <td class="text-center">{{ row.totalHoursWorked }}</td>
          </tr>
        </tbody>
      </table>
    </div>

  </div>
</template>

<script>
import { Alert, SearchableDropdown } from 'wize-admin'
import { AttendanceService, CompanyService, AreaService } from '@/core/services'

const MONTH_NAMES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
]

export default {
  name: 'AttendanceSummary',
  components: { SearchableDropdown },

  data() {
    const now = new Date()
    return {
      rows:        [],
      companies:   [],
      areas:       [],
      downloading: false,
      monthNames:  MONTH_NAMES,
      filters: {
        year:      now.getFullYear(),
        month:     now.getMonth() + 1,
        companyId: null,
        areaId:    null,
      },
    }
  },

  computed: {
    years() {
      const current = new Date().getFullYear()
      return Array.from({ length: 5 }, (_, i) => current - 2 + i)
    },
  },

  methods: {
    async loadData() {
      try {
        this.$store.dispatch('app/loading', true)
        const resp = await AttendanceService.getSummary(this.buildPayload())
        this.rows = resp.payload || []
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al cargar el resumen')
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },

    async downloadXlsx() {
      try {
        this.downloading = true
        const resp = await AttendanceService.downloadSummaryXlsx(this.buildPayload())
        const url  = URL.createObjectURL(new Blob([resp.data]))
        const a    = document.createElement('a')
        a.href     = url
        a.download = `asistencia-${this.filters.year}-${String(this.filters.month).padStart(2, '0')}.xlsx`
        a.click()
        URL.revokeObjectURL(url)
      } catch (e) {
        Alert.error('Error al descargar el Excel')
      } finally {
        this.downloading = false
      }
    },

    buildPayload() {
      const p = { year: this.filters.year, month: this.filters.month }
      if (this.filters.companyId) p.companyId = this.filters.companyId
      if (this.filters.areaId)    p.areaId    = this.filters.areaId
      return p
    },

    onCompanySelected(id) { this.filters.companyId = id; this.loadData() },
    onAreaSelected(id)    { this.filters.areaId    = id; this.loadData() },
  },

  async created() {
    try {
      const [companiesResp, areasResp] = await Promise.all([
        CompanyService.listAll(),
        AreaService.listAll(),
      ])
      this.companies = (companiesResp.payload || []).map(c => ({ key: c.id, label: c.businessName || c.name }))
      this.areas     = (areasResp.payload     || []).map(a => ({ key: a.id, label: a.name }))
    } catch (e) {
      console.error('Error al cargar catálogos', e)
    }
    this.loadData()
  },
}
</script>

<style scoped lang="stylus">
.summary-filters
  .filter-field
    min-width 180px
</style>
